import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { ARTICLES_CATEGORY_LABEL } from "@/lib/arqueo/aggregations";

export type ArqueoSortBy =
  | "time_desc"
  | "time_asc"
  | "amount_desc"
  | "amount_asc";

export interface ArqueoFilterState {
  method: string;
  category: string;
  articleId: string;
  startTime: string;
  endTime: string;
  sortBy: ArqueoSortBy;
}

interface Props {
  filters: ArqueoFilterState;
  onChange: (f: ArqueoFilterState) => void;
  onClear: () => void;
  methods: string[];
  categories: string[];
  articles: { id: string; name: string }[];
}

export const ArqueoFilters = ({
  filters,
  onChange,
  onClear,
  methods,
  categories,
  articles,
}: Props) => {
  const showArticleFilter = filters.category === ARTICLES_CATEGORY_LABEL;
  const hasActive =
    filters.method !== "all" ||
    filters.category !== "all" ||
    filters.startTime !== "" ||
    filters.endTime !== "";

  return (
    <Card className="p-4 bg-card/60 backdrop-blur border-primary/10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary/70">Método de pago</label>
          <Select
            value={filters.method}
            onValueChange={(v) => onChange({ ...filters, method: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black/90 z-50">
              <SelectItem value="all">Todos</SelectItem>
              {methods.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary/70">Categoría</label>
          <Select
            value={filters.category}
            onValueChange={(v) =>
              onChange({ ...filters, category: v, articleId: "all" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black/90 z-50">
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value={ARTICLES_CATEGORY_LABEL}>
                {ARTICLES_CATEGORY_LABEL}
              </SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showArticleFilter && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-primary/70">Artículo</label>
            <Select
              value={filters.articleId}
              onValueChange={(v) => onChange({ ...filters, articleId: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black/90 z-50">
                <SelectItem value="all">Todos</SelectItem>
                {articles.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary/70">Desde (hora)</label>
          <Input
            type="time"
            value={filters.startTime}
            onChange={(e) => onChange({ ...filters, startTime: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary/70">Hasta (hora)</label>
          <Input
            type="time"
            value={filters.endTime}
            onChange={(e) => onChange({ ...filters, endTime: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary/70">Ordenar por</label>
          <Select
            value={filters.sortBy}
            onValueChange={(v) =>
              onChange({ ...filters, sortBy: v as ArqueoFilterState["sortBy"] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black/90 z-50">
              <SelectItem value="time_desc">Hora: más reciente primero</SelectItem>
              <SelectItem value="time_asc">Hora: más antigua primero</SelectItem>
              <SelectItem value="amount_desc">Monto: mayor a menor</SelectItem>
              <SelectItem value="amount_asc">Monto: menor a mayor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasActive && (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="mr-1 h-3.5 w-3.5" /> Limpiar filtros
          </Button>
        </div>
      )}
    </Card>
  );
};
